import React, { useEffect, useState } from 'react';
import { X, Bell, Smartphone, Mail, AlertCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Button from '../ui/Button';
import FrequencyPicker, { type NotificationFrequency } from './FrequencyPicker';
import {
    fetchPreferences,
    upsertPreferences,
    type NotificationChannel,
    type NotificationPreferences,
} from '../../lib/notificationPreferences';
import {
    isStandalone,
    getNotificationPermissionState,
    getCurrentPushSubscription,
} from '../../lib/pwaInstall';
import { toast } from 'sonner';

interface StoryNotificationSettingsProps {
    isOpen: boolean;
    onClose: () => void;
    storyId: string;
    storyTitle: string;
}

const TIME_SLOTS: Array<{ value: number; label: string; sub: string }> = [
    { value: 9, label: 'Mañana', sub: '9:00' },
    { value: 13, label: 'Mediodía', sub: '13:00' },
    { value: 19, label: 'Tarde', sub: '19:00' },
];

const SNOOZE_OPTIONS: Array<{ days: number; label: string }> = [
    { days: 7, label: '1 semana' },
    { days: 14, label: '2 semanas' },
    { days: 30, label: '1 mes' },
];

/**
 * Per-story settings for reminders. Lets the user change frequency,
 * preferred hour, channel (push vs email), pause for a stretch, or turn
 * the reminders off entirely. Reachable from the StoryCard dropdown.
 *
 * The push channel is gated on permission state — if the browser hasn't
 * granted notifications yet (or has denied them), tapping "En el móvil"
 * routes the user to /activate-push instead of saving an unfulfillable
 * intent.
 */
const StoryNotificationSettings: React.FC<StoryNotificationSettingsProps> = ({
    isOpen,
    onClose,
    storyId,
    storyTitle,
}) => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [preferences, setPreferences] = useState<NotificationPreferences | null>(null);
    const [frequency, setFrequency] = useState<NotificationFrequency>('every_2_3_days');
    const [preferredHour, setPreferredHour] = useState<number>(9);
    const [channel, setChannel] = useState<NotificationChannel>('email');
    const [pausedUntil, setPausedUntil] = useState<string | null>(null);
    const [pushReady, setPushReady] = useState(false);

    useEffect(() => {
        if (!isOpen) return;
        let cancelled = false;
        setLoading(true);
        (async () => {
            const prefs = await fetchPreferences(storyId);
            if (cancelled) return;
            if (prefs) {
                setPreferences(prefs);
                setFrequency(prefs.frequency);
                setPreferredHour(prefs.preferred_hour);
                setChannel(prefs.channel);
                setPausedUntil(prefs.paused_until);
            }
            // Push is "ready" only when the browser has granted
            // permission AND there's an active subscription. Otherwise
            // saving channel='push' would just produce email fallbacks.
            try {
                const permission = getNotificationPermissionState();
                if (permission === 'granted') {
                    const existing = await getCurrentPushSubscription();
                    setPushReady(Boolean(existing));
                } else {
                    setPushReady(false);
                }
            } catch {
                setPushReady(false);
            }
            setLoading(false);
        })();
        return () => {
            cancelled = true;
        };
    }, [isOpen, storyId]);

    const handleChannelClick = (next: NotificationChannel) => {
        if (next === 'push' && !pushReady) {
            // Send the user through the install + permission + subscribe
            // flow instead of saving a push intent we can't fulfil.
            navigate(`/activate-push?story=${storyId}`);
            return;
        }
        setChannel(next);
    };

    const handleSnooze = async (days: number) => {
        setSaving(true);
        try {
            const until = new Date();
            until.setDate(until.getDate() + days);
            const isoUntil = until.toISOString();
            const result = await upsertPreferences(storyId, {
                paused_until: isoUntil,
            });
            if (result.success) {
                setPausedUntil(isoUntil);
                toast.success(`Avisos en pausa ${days === 1 ? '1 día' : `${days} días`}.`);
            } else {
                toast.error(result.error || 'No hemos podido guardar la pausa.');
            }
        } finally {
            setSaving(false);
        }
    };

    const handleResume = async () => {
        setSaving(true);
        try {
            const result = await upsertPreferences(storyId, {
                paused_until: null,
            });
            if (result.success) {
                setPausedUntil(null);
                toast.success('Avisos reanudados.');
            } else {
                toast.error(result.error || 'No hemos podido reanudar los avisos.');
            }
        } finally {
            setSaving(false);
        }
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            const result = await upsertPreferences(storyId, {
                frequency,
                preferred_hour: preferredHour,
                channel,
            });
            if (result.success) {
                toast.success('Preferencias guardadas.');
                onClose();
            } else {
                toast.error(result.error || 'No hemos podido guardar.');
            }
        } finally {
            setSaving(false);
        }
    };

    if (!isOpen) return null;

    const standalone = isStandalone();

    return (
        <div
            className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center"
            onClick={onClose}
        >
            <div
                className="fixed inset-0 bg-black/60 backdrop-blur-sm"
                aria-hidden="true"
            />
            <div
                className="relative bg-white rounded-t-3xl sm:rounded-2xl w-full sm:max-w-lg max-h-[92dvh] overflow-y-auto px-5 pt-5 pb-6 shadow-2xl"
                onClick={(e) => e.stopPropagation()}
                role="dialog"
                aria-modal="true"
                aria-labelledby="story-notif-title"
            >
                <button
                    type="button"
                    onClick={onClose}
                    className="!min-h-0 !min-w-0 absolute right-3 top-3 p-1 text-stone-400 hover:text-stone-600"
                    aria-label="Cerrar"
                >
                    <X size={18} />
                </button>

                <div className="flex items-start gap-3 mb-4 pr-6">
                    <div className="w-10 h-10 rounded-full bg-rose-50 flex items-center justify-center text-rose-500 shrink-0">
                        <Bell size={20} />
                    </div>
                    <div>
                        <h2
                            id="story-notif-title"
                            className="text-lg font-medium text-stone-900 leading-snug"
                        >
                            Avisos de esta historia
                        </h2>
                        <p className="text-sm text-stone-600 mt-0.5 leading-relaxed truncate">
                            {storyTitle}
                        </p>
                    </div>
                </div>

                {loading ? (
                    <div className="text-center py-8 text-sm text-stone-500">
                        Cargando preferencias...
                    </div>
                ) : (
                    <div className="space-y-6">
                        {pausedUntil && new Date(pausedUntil) > new Date() && (
                            <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 flex items-start gap-2">
                                <AlertCircle size={16} className="text-amber-600 mt-0.5 shrink-0" />
                                <div className="flex-1 text-sm text-amber-900">
                                    <p className="font-medium">Avisos en pausa</p>
                                    <p className="mt-0.5 text-amber-800">
                                        Hasta el{' '}
                                        {new Date(pausedUntil).toLocaleDateString('es-ES', {
                                            day: 'numeric',
                                            month: 'long',
                                        })}
                                        .
                                    </p>
                                    <button
                                        type="button"
                                        onClick={handleResume}
                                        className="!min-h-0 !min-w-0 text-xs text-amber-900 underline mt-1"
                                    >
                                        Reanudar ahora
                                    </button>
                                </div>
                            </div>
                        )}

                        <div>
                            <FrequencyPicker
                                value={frequency}
                                onChange={setFrequency}
                                legend="¿Con qué frecuencia te avisamos?"
                            />
                        </div>

                        <fieldset className="space-y-2">
                            <legend className="text-sm font-medium text-stone-700">
                                ¿A qué hora te viene mejor?
                            </legend>
                            <div className="grid grid-cols-3 gap-2">
                                {TIME_SLOTS.map((slot) => {
                                    const selected = preferredHour === slot.value;
                                    return (
                                        <button
                                            key={slot.value}
                                            type="button"
                                            onClick={() => setPreferredHour(slot.value)}
                                            className={`px-3 py-2.5 rounded-xl border-2 transition-all text-left ${
                                                selected
                                                    ? 'border-rose-500 bg-rose-50/40'
                                                    : 'border-stone-200 hover:border-stone-300'
                                            }`}
                                        >
                                            <div
                                                className={`text-sm ${
                                                    selected
                                                        ? 'font-medium text-stone-900'
                                                        : 'text-stone-800'
                                                }`}
                                            >
                                                {slot.label}
                                            </div>
                                            <div className="text-xs text-stone-500 mt-0.5">
                                                {slot.sub}
                                            </div>
                                        </button>
                                    );
                                })}
                            </div>
                        </fieldset>

                        <fieldset className="space-y-2">
                            <legend className="text-sm font-medium text-stone-700">
                                Cómo recibirás los avisos
                            </legend>
                            <div className="grid grid-cols-2 gap-2">
                                <button
                                    type="button"
                                    onClick={() => handleChannelClick('push')}
                                    className={`px-3 py-3 rounded-xl border-2 transition-all flex items-center gap-2 text-left ${
                                        channel === 'push'
                                            ? 'border-rose-500 bg-rose-50/40'
                                            : 'border-stone-200 hover:border-stone-300'
                                    }`}
                                >
                                    <Smartphone
                                        size={18}
                                        className={
                                            channel === 'push'
                                                ? 'text-rose-500'
                                                : 'text-stone-500'
                                        }
                                    />
                                    <div>
                                        <div
                                            className={`text-sm ${
                                                channel === 'push'
                                                    ? 'font-medium text-stone-900'
                                                    : 'text-stone-800'
                                            }`}
                                        >
                                            En el móvil
                                        </div>
                                        <div className="text-xs text-stone-500 mt-0.5">
                                            {pushReady
                                                ? 'Al instante'
                                                : 'Configurar'}
                                        </div>
                                    </div>
                                </button>
                                <button
                                    type="button"
                                    onClick={() => handleChannelClick('email')}
                                    className={`px-3 py-3 rounded-xl border-2 transition-all flex items-center gap-2 text-left ${
                                        channel === 'email'
                                            ? 'border-rose-500 bg-rose-50/40'
                                            : 'border-stone-200 hover:border-stone-300'
                                    }`}
                                >
                                    <Mail
                                        size={18}
                                        className={
                                            channel === 'email'
                                                ? 'text-rose-500'
                                                : 'text-stone-500'
                                        }
                                    />
                                    <div>
                                        <div
                                            className={`text-sm ${
                                                channel === 'email'
                                                    ? 'font-medium text-stone-900'
                                                    : 'text-stone-800'
                                            }`}
                                        >
                                            Por correo
                                        </div>
                                        <div className="text-xs text-stone-500 mt-0.5">
                                            Cuando toque
                                        </div>
                                    </div>
                                </button>
                            </div>
                            {channel === 'push' && !pushReady && (
                                <p className="text-xs text-stone-500 leading-relaxed">
                                    {standalone
                                        ? 'Falta dar permiso a Ethernal para enviarte avisos. Lo activamos en el siguiente paso.'
                                        : 'Activa los avisos del navegador desde tu móvil para recibirlos al instante.'}
                                </p>
                            )}
                        </fieldset>

                        <div>
                            <p className="text-sm font-medium text-stone-700 mb-2">
                                ¿Necesitas un descanso?
                            </p>
                            <div className="grid grid-cols-3 gap-2">
                                {SNOOZE_OPTIONS.map((opt) => (
                                    <button
                                        key={opt.days}
                                        type="button"
                                        onClick={() => handleSnooze(opt.days)}
                                        disabled={saving}
                                        className="px-3 py-2 rounded-xl border-2 border-stone-200 hover:border-stone-300 transition-all text-sm text-stone-700"
                                    >
                                        {opt.label}
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div className="pt-2 space-y-2">
                            <Button
                                onClick={handleSave}
                                loading={saving}
                                fullWidth
                                variant="danger"
                            >
                                Guardar
                            </Button>
                            <Button
                                onClick={onClose}
                                fullWidth
                                variant="ghost"
                            >
                                Cancelar
                            </Button>
                        </div>

                        {preferences && preferences.frequency !== 'off' && (
                            <button
                                type="button"
                                onClick={async () => {
                                    setSaving(true);
                                    try {
                                        const result = await upsertPreferences(storyId, {
                                            frequency: 'off',
                                        });
                                        if (result.success) {
                                            toast.success('Avisos desactivados.');
                                            setFrequency('off');
                                            onClose();
                                        }
                                    } finally {
                                        setSaving(false);
                                    }
                                }}
                                className="!min-h-0 !min-w-0 w-full text-center text-xs text-stone-500 hover:text-stone-700 underline"
                            >
                                Desactivar todos los avisos de esta historia
                            </button>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default StoryNotificationSettings;
